<script setup lang="ts">
import type { CarouselEmits, CarouselProps } from "./types";

const props = withDefaults( defineProps<CarouselProps>(), {
  orientation: "horizontal",
  maxSlidesPerView: "3",
  autoplay: 0,
} );

const emits = defineEmits<CarouselEmits>();

const { canScrollNext, canScrollPrev, carouselApi, carouselRef, orientation, scrollNext, scrollPrev } = useProvideCarousel( props, emits );

defineExpose( {
  canScrollNext,
  canScrollPrev,
  carouselApi,
  carouselRef,
  orientation,
  scrollNext,
  scrollPrev,
} );

function onKeyDown ( event: KeyboardEvent ) {
  const prevKey = props.orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
  const nextKey = props.orientation === "vertical" ? "ArrowDown" : "ArrowRight";

  if ( event.key === prevKey ) {
    event.preventDefault();
    scrollPrev();

    return;
  }

  if ( event.key === nextKey ) {
    event.preventDefault();
    scrollNext();
  }
}
</script>

<template>
  <div
    class="carousel"
    :class="{
      'carousel--slides-3': maxSlidesPerView === '3',
      'carousel--slides-4': maxSlidesPerView === '4',
    }"
    role="region"
    aria-roledescription="carousel"
    tabindex="0"
    @keydown="onKeyDown">
    <slot
      :can-scroll-next
      :can-scroll-prev
      :carousel-api
      :carousel-ref
      :orientation
      :scroll-next
      :scroll-prev />
  </div>
</template>

<style scoped lang="scss">
.carousel {
  position: relative;
  text-align: left;
}

.carousel--slides-1,
.carousel--slides-2,
.carousel--slides-3,
.carousel--slides-4 {
  :deep( .carousel-item ) {
    flex-basis: 100%;
  }
}

@media ( min-width: 690px ) {
  .carousel--slides-2,
  .carousel--slides-3,
  .carousel--slides-4 {
    :deep( .carousel-item ) {
      flex-basis: 50%;
    }
  }
}

@media ( min-width: 1000px ) {
  .carousel--slides-3,
  .carousel--slides-4 {
    :deep( .carousel-item ) {
      flex-basis: 33.3333%;

      &:not(
      :nth-child(1),
      :nth-child(2),
      :nth-child(3)
      ) {
        display: none;
      }
    }
  }
}

@media ( min-width: 1200px ) {
  .carousel--slides-4 {
    :deep( .carousel-item ) {
      flex-basis: 25%;
    }
  }
}
</style>
